import React, { useRef } from 'react';
import { X, Bookmark } from 'lucide-react';
import { DocumentShortcut } from '../context/ShortcutContext';
import { SHORTCUT_REORDER_MIME } from '../utils/shortcutDnd';
import { useTranslation } from 'react-i18next';

interface ShortcutTabProps {
  shortcut: DocumentShortcut;
  index: number;
  isActive: boolean;
  /** Primary label shown in bold (code or translated entity name). */
  primaryLabel: string;
  /** Optional secondary label shown smaller beside primary (used for planning routes). */
  secondaryLabel?: string;
  /** Tooltip text for the whole tab. */
  tooltipText: string;
  onNavigate: (shortcut: DocumentShortcut) => void;
  onRemove: (shortcut: DocumentShortcut) => void;
  /** Called when a reorder-drop lands on this tab; source index is passed. */
  onReorder: (fromIndex: number, toIndex: number) => void;
}

const ShortcutTab: React.FC<ShortcutTabProps> = ({
  shortcut,
  index,
  isActive,
  primaryLabel,
  secondaryLabel,
  tooltipText,
  onNavigate,
  onRemove,
  onReorder,
}) => {
  const { t } = useTranslation();
  const isDragOverRef = useRef(false);
  const [isDragOver, setIsDragOver] = React.useState(false);

  // ---- Drag handlers (reorder only — sidebar drops are handled by the bar container) ----

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData(SHORTCUT_REORDER_MIME, String(index));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    // Only react to reorder drags; sidebar drags bubble up to the bar container.
    if (e.dataTransfer.types.includes(SHORTCUT_REORDER_MIME)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (!isDragOverRef.current) {
        isDragOverRef.current = true;
        setIsDragOver(true);
      }
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    // Only clear if leaving this element (not a child).
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      isDragOverRef.current = false;
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    isDragOverRef.current = false;
    setIsDragOver(false);

    if (!e.dataTransfer.types.includes(SHORTCUT_REORDER_MIME)) return;

    // Stop propagation so the bar-level sidebar-drop handler doesn't also fire.
    e.stopPropagation();
    e.preventDefault();

    const raw = e.dataTransfer.getData(SHORTCUT_REORDER_MIME);
    const sourceIndex = parseInt(raw, 10);
    if (isNaN(sourceIndex) || sourceIndex === index) return;

    onReorder(sourceIndex, index);
  };

  const handleDragEnd = () => {
    isDragOverRef.current = false;
    setIsDragOver(false);
  };

  // ---- Keyboard navigation ----

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onNavigate(shortcut);
    }
  };

  // ---- Styling ----

  // Flat pill: same box shape/size in both states (no growth/shift on activate).
  const tabBase =
    'group relative flex items-center gap-1.5 px-3 py-1 rounded-full border ' +
    'transition-all duration-150 cursor-pointer whitespace-nowrap flex-shrink-0 text-xs ' +
    'focus:outline-none focus:ring-2 focus:ring-blue-500 select-none';

  const activeClass = 'bg-blue-50 text-blue-700 border-blue-400 font-semibold';

  const inactiveClass =
    'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:border-blue-300';

  const dropHighlight = isDragOver ? 'ring-2 ring-blue-400 ring-inset' : '';

  return (
    <div
      role="tab"
      tabIndex={0}
      aria-selected={isActive}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
      onClick={() => onNavigate(shortcut)}
      onKeyDown={handleKeyDown}
      className={`${tabBase} ${isActive ? activeClass : inactiveClass} ${dropHighlight}`}
      title={tooltipText}
    >
      <Bookmark size={12} />

      {/* Tab label */}
      <span className="leading-none">
        {primaryLabel}
        {secondaryLabel && (
          <span className="ml-1 text-xs font-normal opacity-75">
            - {secondaryLabel}
          </span>
        )}
      </span>

      {/* Remove button — hover-only so it doesn't compete with the open-tab close button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(shortcut);
        }}
        className="p-0.5 rounded transition-opacity opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-red-600 hover:bg-red-50"
        title={t('shortcuts.removeShortcut') || 'Remove shortcut'}
      >
        <X size={12} />
      </button>
    </div>
  );
};

export default ShortcutTab;
